import { onScopeDispose } from 'vue';

/**
 * Keyboard access to icon buttons nested inside a reka-ui menu row.
 *
 * A reka `DropdownMenuItem` is a roving-tabindex list entry, and anything
 * nested inside it is invisible to the keyboard. Measured against reka-ui
 * 2.9.7 (node_modules/reka-ui/dist/Menu): MenuContentImpl cancels Tab for
 * everything inside `[data-reka-menu-content]`; its arrow handling walks only
 * `[data-reka-collection-item]` rows, ejecting focus from a nested button back
 * to a row; and MenuItem turns Enter/Space on ANY descendant into a click on
 * the ROW — so Enter on a focused trash can loaded the view and closed the
 * menu. Rename and delete shipped like that; the rollover styling only made it
 * visible, by fading three buttons in for a keyboard user who could not press
 * any of them.
 *
 * The shape: the row stays reka's tab stop. Right Arrow on a focused row moves
 * DOM focus to the row's first enabled button; Right/Left move between the
 * buttons (Right wraps, Left from the first returns to the row); Escape goes
 * back to the row; Enter/Space press THAT button; Up/Down/Home/End hand focus
 * back to the row and are then left for reka, which moves to the adjacent row
 * as it normally would. Every key this owns is stopped at the button, and that
 * works because reka binds its listeners on the row and the content in the
 * bubble phase. A printable letter is deliberately not stopped, so reka's
 * typeahead still jumps between rows from a button.
 *
 * Nothing advertises the affordance: Right Arrow has to be tried. There was an
 * off-screen hint for screen readers, removed because this app has no
 * screen-reader users — if that changes, describe the menu trigger rather than
 * every row, which re-read it at each arrow press.
 *
 * Buttons carry `tabindex="-1"` so Tab (which reka cancels anyway) never
 * counts them as stops, and the row's `focus-within` keeps its highlight and
 * its revealed actions while one of them holds focus.
 */

/**
 * Marks a button as part of a row's Right/Left ring. Explicit, rather than
 * "every button in the row": a row that later gains an unrelated button — a
 * toggle, a second shadcn Button, a nested trigger — would otherwise join the
 * ring silently, with no test failing.
 */
export const MENU_ROW_ACTION_ATTR = 'data-menu-row-action';

/**
 * The attribute's value for an action that can REMOVE its own row. Only these
 * arm the focus repair, and only these need it: sharing and renaming leave the
 * row where it is.
 */
export const MENU_ROW_ACTION_REMOVES = 'remove';

// Every row role reka can render, not just `menuitem`: a checkbox or radio item
// renders `menuitemcheckbox` / `menuitemradio`, and matching only the first left
// focus stranded on a button whose keys then fell through to reka.
const ROW_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';
const MENU_SELECTOR = '[data-reka-menu-content]';

function actionButtonsOf(row: HTMLElement): HTMLButtonElement[] {
  return Array.from(row.querySelectorAll<HTMLButtonElement>(`[${MENU_ROW_ACTION_ATTR}]:not([disabled])`));
}

function rowOf(element: HTMLElement): HTMLElement | null {
  return element.closest<HTMLElement>(ROW_SELECTOR);
}

/**
 * Put focus back in the menu when the action removed the row it lived in.
 *
 * Deleting a row unmounts the button that is holding focus, and focus falls to
 * `<body>`: the menu stays open and stops answering every arrow key, so a user
 * deleting two views has to Escape and reopen between them. reka does not
 * recover it — FocusScope's fallback focuses `[data-reka-popper-content-wrapper]`,
 * which has no tabindex, so the call does nothing. Focus goes to whichever row
 * slid into the deleted one's place, or the last row, or the menu itself.
 */
function repairFocusAfter(row: HTMLElement, menu: HTMLElement, index: number): void {
  // Watched rather than timed. How long the parent takes to drop the row is not
  // ours to know — an optimistic cache patch is one tick, a server round trip is
  // hundreds — and a timer that fires first sees the row still present and does
  // nothing, which is the bug it was meant to repair.
  const observer = new MutationObserver(() => {
    if (row.isConnected) {
      return;
    }
    observer.disconnect();
    clearTimeout(giveUp);
    // Something else may already have taken focus; only step in if nothing did.
    if (menu.contains(document.activeElement)) {
      return;
    }
    const rows = Array.from(menu.querySelectorAll<HTMLElement>(ROW_SELECTOR));
    (rows[index] ?? rows[rows.length - 1] ?? menu).focus();
  });
  observer.observe(menu, { childList: true, subtree: true });
  // The action may not remove anything at all — sharing does not. Stop watching.
  const giveUp = setTimeout(() => observer.disconnect(), 2000);
}

export function useMenuRowActionKeys() {
  /**
   * The key currently held down after activating one of these buttons, if any.
   *
   * Why this exists: pressing Enter on a trash can deletes the row, the focus
   * repair below moves focus to the row that slid into its place, and the OS
   * repeat of that same unbroken hold is then delivered to a LIVE menu row.
   * reka's MenuItem has no repeat guard, so it clicked that row and loaded a
   * saved view the user never asked for — one hold, a delete AND a silent
   * replacement of their filter and columns.
   *
   * Why it is a capture listener on `window` rather than a check inside
   * `onRowKeydown`: reka registers its own keydown on the row element before
   * ours (measured — an `Enter` on a focused row still reached MenuItem's
   * handler and clicked the row even with `stopImmediatePropagation` in our row
   * handler). Same-element listeners run in registration order, and neither
   * `stopPropagation` nor `preventDefault` reaches a listener that already ran.
   * A capture listener on `window` is above every row, so it runs first no
   * matter which element the repeat lands on — and that matters here precisely
   * because the element the press started on no longer exists.
   */
  let heldActionKey: string | null = null;

  /** Kill the whole dispatch, not just the bubble: reka's listener is a sibling. */
  function swallowHeldActionKey(event: KeyboardEvent): void {
    if (event.key !== heldActionKey) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function stopHolding(): void {
    heldActionKey = null;
    window.removeEventListener('keydown', swallowHeldActionKey, true);
    window.removeEventListener('keyup', releaseActionKey, true);
    window.removeEventListener('blur', stopHolding);
  }

  function releaseActionKey(event: KeyboardEvent): void {
    if (event.key === heldActionKey) {
      stopHolding();
    }
  }

  function holdActionKey(key: string): void {
    if (heldActionKey !== null) {
      return;
    }
    heldActionKey = key;
    window.addEventListener('keydown', swallowHeldActionKey, true);
    // On window, because by the time the key comes up the button it was pressed
    // on has been unmounted and can no longer be the target of anything.
    window.addEventListener('keyup', releaseActionKey, true);
    // Alt-Tab away mid-hold and the keyup is delivered to the other window, so
    // ours never arrives. Without this the guard would stick for the life of the
    // page and eat every Enter in the app — far worse than the bug it prevents.
    window.addEventListener('blur', stopHolding);
  }

  onScopeDispose(stopHolding);

  /** Bind to the row (`@keydown` on the DropdownMenuItem). */
  function onRowKeydown(event: KeyboardEvent): void {
    // Modifiers are the browser's: Alt+Right is Forward, and reka guards its own
    // handlers the same way.
    if (event.key !== 'ArrowRight' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const row = event.currentTarget as HTMLElement;
    // Only when the ROW itself holds focus. A Right Arrow inside the inline
    // rename input is the caret moving, and its own handler stops it anyway.
    if (event.target !== row) {
      return;
    }
    const [first] = actionButtonsOf(row);
    if (!first) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    first.focus();
  }

  /** Bind to every action button in the row (`@keydown`). */
  function onActionKeydown(event: KeyboardEvent): void {
    const button = event.currentTarget as HTMLButtonElement;
    const row = rowOf(button);
    if (!row) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case ' ': {
        // ALWAYS stopped, modifiers included. reka's MenuItem acts on a bare
        // Enter from any descendant, so letting a modified one fall through
        // meant Ctrl+Enter on the trash can loaded the view instead.
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) {
          return;
        }
        holdActionKey(event.key);
        const menu = row.closest<HTMLElement>(MENU_SELECTOR);
        const index = menu
          ? Array.from(menu.querySelectorAll<HTMLElement>(ROW_SELECTOR)).indexOf(row)
          : -1;
        const removesTheRow = button.getAttribute(MENU_ROW_ACTION_ATTR) === MENU_ROW_ACTION_REMOVES;
        button.click();
        if (removesTheRow && menu && index >= 0) {
          repairFocusAfter(row, menu, index);
        }
        return;
      }
      case 'ArrowRight':
      case 'ArrowLeft': {
        // Alt+Arrow is the browser's Back and Forward.
        if (event.altKey || event.ctrlKey || event.metaKey) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const buttons = actionButtonsOf(row);
        const index = buttons.indexOf(button);
        const next = event.key === 'ArrowRight'
          ? buttons[(index + 1) % buttons.length]
          : buttons[index - 1];
        (next ?? row).focus();
        return;
      }
      case 'Escape':
        // Back to the row, and NOT to reka — an unstopped Escape closes the menu.
        event.preventDefault();
        event.stopPropagation();
        row.focus();
        return;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Home':
      case 'End':
        // Hand focus back to the row and let the key bubble: reka then moves to
        // the adjacent row from there, instead of ejecting to the first row.
        row.focus();
        return;
      default:
        // A printable key is reka's typeahead, and it resolves where to search
        // FROM by looking at the active element. A button is not a collection
        // item, so searching from one restarted at the top of the menu and a
        // user could never type past the row they were on. Hand focus back
        // first, then let it bubble — the same trick as the arrows above.
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          row.focus();
        }
        return;
    }
  }

  return { onRowKeydown, onActionKeydown };
}
